// Pure derivation of GlowSpine's `lines[]` from a lane's forecast data.
//
// A "lane" is one root forecast + its branch runs (the timeline renders one lane
// normally, several in compare mode). laneLayout gives the per-lane `spineY` and
// the per-branch `branchYs`; this maps those + the run fields into the normalized
// line shape GlowSpine draws. No React, no time, no DOM — unit-tested; `birth`
// (an animation timestamp) is injected via `birthOf` so callers own entrance
// timing and this stays deterministic.
//
// Field names are the real run-store ones (see lib/forecast/model.js /
// selectors.ts and the features/timeline/components/ForecastLane.jsx adapter):
//   root.contract.resolution_date   — main FORECAST x
//   b.runId                          — branch id
//   b.parentRunId                    — parent branch id (=== rootId ⇒ forks off main)
//   b.forkDate                       — where the branch peels away (splitX)
//   b.contract.resolution_date       — branch's own FORECAST x (falls back to root's)
//   b.modification                   — human branch label

const MAIN_ID = "main";

// Glow-spine visual tunables (undulation/glow/edge-fade). Lives here — a pure,
// React-free module — rather than in the GlowSpine component file so the
// component can stay a components-only export (react-refresh) and App.jsx can
// seed its persisted controls from these without importing the component.
export const GLOW_DEFAULTS = {
  amp: 2.8, // undulation amplitude, px. 0 = dead-flat line.
  speed: 0.15, // flow speed of the undulation. 0 = frozen.
  fadeLeft: 0.49, // left edge fade, fraction of width (past horizon).
  fadeRight: 0.06, // right edge fade, fraction of width (trailing future).
  glow: 1.85, // glow strength multiplier. 0 = no halo (flat line).
  freq: 7, // spatial frequency of the wave across the full width.
};

// How far each line extends PAST its resolution (forecast line) before fading
// out, and the width over which it fades — a soft trailing wisp, matching the
// dashed future line's trail rather than ending hard at the forecast marker.
export const TRAIL_PX = 64;

// Golden-angle phase step (radians) between successive branch undulations, so
// each branch ripples out of sync with the main spine and its siblings.
const PHASE_STEP = 2.39996;

// Branch peel-away transition width (px), scaled to the vertical drop between the
// parent spine and the branch lane. SHARED by GlowSpine's curve math and
// buildGlowLines: the model places each branch's split so the peel LANDS on the
// fork/trigger x (split = forkX − TW), and GlowSpine draws the S-curve over
// [split, split+TW] = [forkX−TW, forkX]. Keeping one definition keeps the curve's
// landing point and the trigger position exactly aligned.
export function twFor(drop) {
  const d = Math.abs(drop);
  return Math.max(150, Math.min(280, d * 3.2));
}

/**
 * Build the GlowSpine `lines[]` for one lane.
 *
 * @param {object}   root            the lane's root forecast (needs contract.resolution_date)
 * @param {string}   rootId          the root run id (a branch whose parentRunId === this forks off main)
 * @param {object[]} branches        the lane's branch runs, in render order
 * @param {number}   spineY          this lane's main spine y (laneLayout spineY)
 * @param {number[]} branchYs        per-branch spine y, aligned to `branches` (laneLayout branchYs)
 * @param {function} dayToScreen     (dateLike) => x pixel (the timeline's view-relative mapping)
 * @param {boolean}  mainGenerating  whether the main line is mid-forecast (shows the label, no events)
 * @param {function} branchGenerating (branchRun) => boolean — mid-forecast flag per branch
 * @returns {object[]} GlowSpine lines; [] when there is nothing to draw
 *
 * Note: entrance-animation `birth` timestamps are NOT set here — GlowSpine tracks
 * first-seen time per line id inside its rAF loop (an effect), so this stays a
 * pure, timestamp-free render-phase computation.
 */
export function buildGlowLines({
  root,
  rootId,
  branches = [],
  spineY,
  branchYs = [],
  dayToScreen,
  mainGenerating = false,
  branchGenerating = () => false,
}) {
  const mainRes = root?.contract?.resolution_date;
  if (!root || mainRes == null || typeof dayToScreen !== "function") return [];

  const mainEndX = dayToScreen(mainRes);
  const lines = [
    {
      id: MAIN_ID,
      parentId: null,
      splitX: 0,
      yOffset: spineY,
      endX: mainEndX + TRAIL_PX, // trail past the forecast line, then fade
      label: null,
      generating: !!mainGenerating,
      phase: 0,
    },
  ];

  // Ids present in this lane, so a branch-of-branch parent that resolves within
  // the lane keeps its real parent; anything else (or a top-level fork) attaches
  // to main — never leave a parentId GlowSpine can't resolve (it would throw).
  const laneIds = new Set(branches.map((b) => b.runId));
  // Spine y per line id (main + each branch), so we can compute each branch's
  // drop-to-parent — and thus its peel-away width TW — the same way GlowSpine does.
  const yById = { [MAIN_ID]: spineY };
  branches.forEach((b, i) => {
    yById[b.runId] = branchYs[i];
  });

  branches.forEach((b, i) => {
    const rawParent = b.parentRunId;
    const parentId =
      rawParent && rawParent !== rootId && laneIds.has(rawParent) ? rawParent : MAIN_ID;
    const branchRes = b?.contract?.resolution_date ?? mainRes;
    // Mirror the dashed-spine path's fork-date validation (ForecastLane's
    // isValidForkDate): a branch with a null/invalid forkDate, a non-numeric
    // branchYs[i], or an unmappable resolution yields NaN coordinates, which
    // GlowSpine feeds straight into its SVG path math and breaks rendering.
    // Skip such branches so the glow layer stays resilient rather than drawing
    // a NaN line.
    const forkX = dayToScreen(b.forkDate);
    const yOffset = branchYs[i];
    const endX = dayToScreen(branchRes) + TRAIL_PX; // trail past the branch's forecast line
    if (!Number.isFinite(forkX) || !Number.isFinite(yOffset) || !Number.isFinite(endX)) return;
    // Land the peel-away ON the fork/trigger x: start the split TW px EARLIER so
    // the S-curve has finished leveling onto the branch lane by the time it
    // reaches the trigger (rather than only beginning to peel there). TW here
    // must match GlowSpine's — same twFor, same parent-settled y (parent's lane,
    // or spineY when the parent is main).
    const parentSettledY = yById[parentId] ?? spineY;
    const TW = twFor(yOffset - parentSettledY);
    lines.push({
      id: b.runId,
      parentId,
      splitX: forkX - TW,
      yOffset,
      endX,
      // The timeline labels branches by ordinal ("Branch 1", "Branch 2"), matching
      // the dashed-spine UI this replaces — NOT b.modification (that assumption
      // text is the tooltip's, and is often too long for the on-line label gap).
      label: `Branch ${i + 1}`,
      generating: !!branchGenerating(b),
      phase: (i + 1) * PHASE_STEP, // desync this branch's undulation
    });
  });

  return lines;
}
